import { z } from 'zod';
import { UserError } from '../../errors';
import { type Tool, type ToolCallDetails, tool } from '../../tool';
import { withSandboxSpan } from '../runtime/spans';
import { formatExecResponse } from '../shared/output';
import {
  Capability,
  type ConfigureCapabilityTools,
  requireBoundSession,
} from './base';

const DEFAULT_OUTPUT_PREVIEW_BYTES = 30_000;
const MAX_OUTPUT_PREVIEW_BYTES = 150_000;

/**
 * 一条白名单命令经校验后的产物:要 spawn 的二进制 + argv(不含二进制名),以及
 * argv 中哪些下标是路径(需被约束在工作区内)。argv 里已插入的 flag / `--` 原样保留。
 */
type BuiltCommand = {
  command: string;
  args: string[];
  pathArgs: { index: number; forWrite: boolean }[];
};

type CommandSpec = {
  /** 展示给模型的用法,用于工具描述。 */
  usage: string;
  /** 逐条写死的校验:把模型给的 rawArgs 校验并构造成 BuiltCommand,非法即抛 UserError。 */
  build: (rawArgs: string[]) => BuiltCommand;
};

// ─────────────────────────────────────────────────────────────────────────────
// 白名单。命令名单有限,每条单独写死规则,**不建通用约束框架**。
//
// 安全不变量(新增命令前必须逐条确认):
//   1. 绝不放入能执行任意代码的命令/模式(node 只允许 `--check`;不放 sh/bash/python/
//      make/git 等)。同二进制多模式靠"第一个参数写死"封死(见 buildNodeCheck)。
//   2. 绝不放入能"创建/保留符号链接"的命令或 flag(不放 ln;cp 只允许 -r/-R,禁
//      -a/-d/-P/--no-dereference)。这维持 agent 结构性无法制造 symlink 来源,从而
//      让 symlink 越界(open(2) 跟随中间段软链)在"工作区初始内容第一方可信"前提下
//      成为可接受的残余风险。新增 tar/unzip/rsync 等能解出 symlink 的命令时必须重审。
//   3. 路径参数一律经 session.resolveSandboxPath 约束在工作区内(见 execForegroundArgv)。
// ─────────────────────────────────────────────────────────────────────────────
const ALLOWED_COMMANDS: Record<string, CommandSpec> = {
  cp: {
    usage: 'cp [-r|-R] <src...> <dest>  复制文件/目录(均限工作区内)',
    build: buildCp,
  },
  mv: { usage: 'mv <src> <dest>  移动/重命名(均限工作区内)', build: buildMv },
  mkdir: {
    usage: 'mkdir [-p] <dir...>  新建目录(限工作区内)',
    build: buildMkdir,
  },
  node: {
    usage: 'node --check <file>  仅语法检查 JS(不执行)',
    build: buildNodeCheck,
  },
};

const RUN_COMMAND_INSTRUCTIONS = [
  '当你需要在工作区内做文件操作(复制模板、移动文件、建目录、校验 JS 语法)时,用 `run_command`。',
  '它不是 bash:不支持管道/重定向/变量/通配符/命令替换。把命令写成 argv 数组。',
  '可用命令:',
  ...Object.values(ALLOWED_COMMANDS).map((spec) => `- ${spec.usage}`),
  '所有路径参数都必须落在工作区内,越界会被拒绝。',
].join('\n');

export type RestrictedShellArgs = {
  configureTools?: ConfigureCapabilityTools;
  outputPreviewBytes?: number;
};

class RestrictedShellCapability extends Capability {
  readonly type = 'restrictedShell';
  private readonly configureTools?: ConfigureCapabilityTools;
  private readonly outputPreviewBytes: number;

  constructor(args: RestrictedShellArgs = {}) {
    super();
    this.configureTools = args.configureTools;
    this.outputPreviewBytes = normalizeOutputPreviewBytes(
      args.outputPreviewBytes,
    );
  }

  override tools(): Tool<any>[] {
    const session = requireBoundSession(this.type, this._session);
    if (!session.execForegroundArgv) {
      throw new UserError(
        'RestrictedShell sandbox sessions must provide execForegroundArgv().',
      );
    }

    const commandNames = Object.keys(ALLOWED_COMMANDS) as [string, ...string[]];

    const tools: Tool<any>[] = [
      tool({
        name: 'run_command',
        description: [
          '在沙箱工作区内执行有限的白名单命令',
          '注意：这不是 shell，因此不支持管道、重定向、通配符、变量或命令替换',
          '可用命令如下:',
          ...Object.values(ALLOWED_COMMANDS).map((spec) => `  ${spec.usage}`),
        ].join('\n'),
        parameters: z.object({
          command: z.enum(commandNames).describe('要运行的命令'),
          args: z
            .array(z.string())
            .describe(
              '参数以 argv 数组形式传入, 例如 ["-r","template","dst"]. 不支持 shell 语法',
            ),
          description: z.string().optional().describe('对该命令功能的简短描述'),
        }),
        execute: async (
          {
            command,
            args,
            description,
          }: { command: string; args: string[]; description?: string },
          _context?: unknown,
          details?: ToolCallDetails,
        ): Promise<string> => {
          const spec = ALLOWED_COMMANDS[command];
          if (!spec) {
            throw new UserError(`命令 "${command}" 不在白名单。`);
          }
          const built = spec.build(args);
          return await withSandboxSpan(
            'sandbox.run_command',
            {
              cmd: [built.command, ...built.args].join(' '),
              description,
              run_as: this._runAs,
            },
            async () => {
              const result = await session.execForegroundArgv!({
                command: built.command,
                args: built.args,
                pathArgs: built.pathArgs,
                outputPreviewBytes: this.outputPreviewBytes,
                outputId: details?.toolCall?.callId,
                runAs: this._runAs,
              });
              return formatExecResponse(result);
            },
          );
        },
      }),
    ];

    return this.configureTools ? this.configureTools([...tools]) : tools;
  }

  override instructions(): string {
    return RUN_COMMAND_INSTRUCTIONS;
  }
}

export type RestrictedShell = RestrictedShellCapability;

export function restrictedShell(
  args: RestrictedShellArgs = {},
): RestrictedShell {
  return new RestrictedShellCapability(args);
}

/**
 * 取出开头连续的、属于 allowed 集合的 flag;其余 token 视为路径。第一个路径 token
 * 若仍以 '-' 开头,判为未授权选项并拒绝(后续路径 token 以 '-' 开头则放行——最终
 * argv 里会插入 `--`,二进制不会把它误当 flag)。
 */
function takeLeadingFlags(
  raw: string[],
  allowed: Set<string>,
  command: string,
): { flags: string[]; paths: string[] } {
  const flags: string[] = [];
  let index = 0;
  while (index < raw.length && allowed.has(raw[index])) {
    flags.push(raw[index]);
    index += 1;
  }
  const paths = raw.slice(index);
  if (paths.length > 0 && paths[0].startsWith('-')) {
    const allowedList = [...allowed].join(', ') || '无';
    throw new UserError(
      `${command} 不允许选项 "${paths[0]}"。仅允许的 flag: ${allowedList}`,
    );
  }
  for (const path of paths) {
    if (path.length === 0) {
      throw new UserError(`${command} 的路径参数不能为空。`);
    }
  }
  return { flags, paths };
}

/** 给 [...flags, '--', ...paths] 这种 argv 计算每个路径的下标。 */
function pathIndicesAfter(
  flagCount: number,
  paths: string[],
  forWrite: (i: number) => boolean,
): { index: number; forWrite: boolean }[] {
  const base = flagCount + 1; // +1 跳过 '--'
  return paths.map((_, i) => ({ index: base + i, forWrite: forWrite(i) }));
}

function buildCp(raw: string[]): BuiltCommand {
  const { flags, paths } = takeLeadingFlags(raw, new Set(['-r', '-R']), 'cp');
  if (paths.length < 2) {
    throw new UserError('cp 需要至少一个源和一个目标路径。');
  }
  return {
    command: 'cp',
    args: [...flags, '--', ...paths],
    // 目标(最后一个)可写,其余为只读源。
    pathArgs: pathIndicesAfter(
      flags.length,
      paths,
      (i) => i === paths.length - 1,
    ),
  };
}

function buildMv(raw: string[]): BuiltCommand {
  const { flags, paths } = takeLeadingFlags(raw, new Set(), 'mv');
  if (paths.length !== 2) {
    throw new UserError('mv 需要且仅需要 源 与 目标 两个路径。');
  }
  return {
    command: 'mv',
    args: [...flags, '--', ...paths],
    pathArgs: pathIndicesAfter(flags.length, paths, (i) => i === 1),
  };
}

function buildMkdir(raw: string[]): BuiltCommand {
  const { flags, paths } = takeLeadingFlags(raw, new Set(['-p']), 'mkdir');
  if (paths.length < 1) {
    throw new UserError('mkdir 需要至少一个目录路径。');
  }
  return {
    command: 'mkdir',
    args: [...flags, '--', ...paths],
    pathArgs: pathIndicesAfter(flags.length, paths, () => true),
  };
}

function buildNodeCheck(raw: string[]): BuiltCommand {
  // :node 只允许 `node --check <file>`。第一个参数不是 --check 直接拒,
  if (raw[0] !== '--check') {
    throw new UserError(
      'node 仅允许 `node --check <file>`:第一个参数必须是 --check。',
    );
  }
  const paths = raw.slice(1);
  if (paths.length !== 1) {
    throw new UserError('node --check 需要且仅需要一个文件路径。');
  }
  if (paths[0].length === 0 || paths[0].startsWith('-')) {
    throw new UserError('node --check 的文件路径无效。');
  }
  // node 不加 `--`(其路径已校验不以 '-' 开头,无 flag 注入风险)。
  return {
    command: 'node',
    args: ['--check', paths[0]],
    pathArgs: [{ index: 1, forWrite: false }],
  };
}

function normalizeOutputPreviewBytes(value?: number): number {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return DEFAULT_OUTPUT_PREVIEW_BYTES;
  }
  return Math.min(Math.floor(value), MAX_OUTPUT_PREVIEW_BYTES);
}

export const __testables = {
  ALLOWED_COMMANDS,
  buildCp,
  buildMv,
  buildMkdir,
  buildNodeCheck,
};
